/**
 * ArcOrb — the single canonical "Arc is working" symbol.
 *
 * THIS IS THE ONLY PLACE the orb visual is defined. Edit this file and
 * the change propagates everywhere in the app. Do not replicate the SVG
 * inline elsewhere — always import and render <ArcOrb>.
 *
 * Visual source: /orb-pulse.svg (design reference; not imported at
 * runtime — the SVG is rendered inline so gradient IDs can be scoped
 * per-instance, allowing multiple orbs on screen without conflicts).
 *
 * Usage:
 *   <ArcOrb size={32} />                        // working (default)
 *   <ArcOrb size={48} state="done" onDone={fn} /> // flash then fade
 *   <ArcOrb size={20} state="error" />           // red pulse then fade
 *   <ArcOrb size={16} state="idle" />            // static dim dot
 *
 * Size branching:
 *   size <  32 — shrinks the expand/contract a bit so the orb doesn't bleed
 *                outside the SVG box via bloom filters at icon scale
 *   size >= 32 — full expand/contract orb pulse
 *
 * States:
 *   working — bright point expands to fill the orb, contracts, repeats
 *   done    — brightens then fades out; calls onDone after ~650ms
 *   error   — pulses red-orange then fades
 *   idle    — static dim dot
 *
 * Helper hook for managed done-flash transitions:
 *   const { orbState, orbVisible, triggerDone } = useArcOrbState();
 *   // call triggerDone() when Arc finishes; orbVisible tells you when to unmount
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';

/** Manages working → done-flash → hidden lifecycle. */
export function useArcOrbState(initiallyVisible = false) {
  const [orbVisible, setOrbVisible] = useState(initiallyVisible);
  const [orbState, setOrbState] = useState('working');

  const show        = useCallback(() => { setOrbState('working'); setOrbVisible(true); }, []);
  const triggerDone = useCallback(() => setOrbState('done'),  []);
  const triggerError= useCallback(() => setOrbState('error'), []);
  const hide        = useCallback(() => { setOrbVisible(false); setOrbState('working'); }, []);

  return { orbVisible, orbState, show, triggerDone, triggerError, hide };
}

const ORB_ID_COUNTER = (() => { let n = 0; return () => ++n; })();

export default function ArcOrb({ size = 32, state = 'working', onDone, className = '', style = {} }) {
  const id = useRef(`arc-orb-${ORB_ID_COUNTER()}`).current;

  const isError   = state === 'error';
  const isDone    = state === 'done';
  const isIdle    = state === 'idle';
  const isWorking = state === 'working';
  const isSmall   = size < 32;

  useEffect(() => {
    if (isDone && onDone) {
      const t = setTimeout(onDone, 650);
      return () => clearTimeout(t);
    }
  }, [isDone, onDone]);

  // Color palette per state
  const c0 = isError ? '#ff8040' : '#ffffff';         // hot center
  const c1 = isError ? '#e04010' : '#c89aff';         // mid band
  const c2 = isError ? '#8020a0' : '#7040d8';         // body
  const c3 = isError ? '#500800' : '#2a0870';         // outer

  // CSS flash animation applied to the whole group for done/error transitions
  const flashStyle = isDone  ? { animation: `${id}-done 0.6s ease-out forwards` }
                   : isError ? { animation: `${id}-err  0.6s ease-out forwards` }
                   : {};

  const dur    = '4s';
  const spline = '0.42 0 0.58 1';
  const kt     = '0;0.5;1';
  const ks     = `${spline}; ${spline}`;

  // Small sizes shrink the expand/contract so the orb doesn't bleed
  // outside the SVG box via the bloom/ambient filters.
  const maxOrb     = isSmall ? 220 : 481;
  const maxAmbient = isSmall ? 200 : 660;
  const maxCore    = isSmall ?  50 : 120;

  return (
    <svg
      width={size} height={size} viewBox="0 0 680 680"
      xmlns="http://www.w3.org/2000/svg"
      className={`arc-orb arc-orb--${state} ${className}`}
      style={{ display: 'block', flexShrink: 0, borderRadius: '50%', overflow: 'hidden', ...style }}
      aria-label="Arc is working" role="img"
    >
      <defs>
        <radialGradient id={`${id}-pulse`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={c0} stopOpacity="1"/>
          <stop offset="10%"  stopColor={c0} stopOpacity="1"/>
          <stop offset="28%"  stopColor={c1} stopOpacity="0.96"/>
          <stop offset="50%"  stopColor={c2} stopOpacity="0.82"/>
          <stop offset="72%"  stopColor={c3} stopOpacity="0.55"/>
          <stop offset="88%"  stopColor="#0d0330" stopOpacity="0.22"/>
          <stop offset="100%" stopColor="#000000" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`${id}-ambient`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={c0} stopOpacity="0.75"/>
          <stop offset="35%"  stopColor={c1} stopOpacity="0.45"/>
          <stop offset="70%"  stopColor={c2} stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#000000" stopOpacity="0"/>
        </radialGradient>
        {/* Heavy blur for the canvas-flood ambient wash */}
        <filter id={`${id}-amb`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="55"/>
        </filter>
        {/* Bloom on main orb */}
        <filter id={`${id}-bloom`} x="-35%" y="-35%" width="170%" height="170%">
          <feGaussianBlur stdDeviation="22" result="b"/>
          <feBlend in="SourceGraphic" in2="b" mode="screen"/>
        </filter>
        {/* Tight glow on the hot center point */}
        <filter id={`${id}-pt`} x="-300%" y="-300%" width="700%" height="700%">
          <feGaussianBlur stdDeviation="7" result="b"/>
          <feBlend in="SourceGraphic" in2="b" mode="screen"/>
        </filter>
      </defs>

      <style>{`
        @keyframes ${id}-done {
          0%   { opacity: 1; }
          40%  { opacity: 1; filter: brightness(2.5); }
          100% { opacity: 0; }
        }
        @keyframes ${id}-err {
          0%   { opacity: 1; }
          30%  { opacity: 1; filter: saturate(4) hue-rotate(-40deg) brightness(1.5); }
          100% { opacity: 0; }
        }
      `}</style>

      <g opacity={isIdle ? 0.3 : 1} style={flashStyle}>
        {/* Dark base */}
        <circle cx="340" cy="340" r="340" fill="#08050f"/>

        {/* Ambient light wash — large blurred halo that floods the whole viewBox at peak */}
        {isWorking ? (
          <circle cx="340" cy="340" fill={`url(#${id}-ambient)`} filter={`url(#${id}-amb)`}>
            <animate attributeName="r"       values={`0;${maxAmbient};0`} dur={dur} calcMode="spline" keyTimes={kt} keySplines={ks} repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0;1;0"   dur={dur} calcMode="spline" keyTimes={kt} keySplines={ks} repeatCount="indefinite"/>
          </circle>
        ) : !isIdle && (
          <circle cx="340" cy="340" r="220" fill={`url(#${id}-ambient)`} filter={`url(#${id}-amb)`} opacity="0.6"/>
        )}

        {/* Main expanding orb — at peak r=481 covers every corner of the 680×680 viewBox */}
        {isWorking ? (
          <circle cx="340" cy="340" fill={`url(#${id}-pulse)`} filter={`url(#${id}-bloom)`}>
            <animate attributeName="r" values={`0;${maxOrb};0`} dur={dur} calcMode="spline" keyTimes={kt} keySplines={ks} repeatCount="indefinite"/>
          </circle>
        ) : !isIdle && (
          <circle cx="340" cy="340" r="185" fill={`url(#${id}-pulse)`} filter={`url(#${id}-bloom)`}/>
        )}

        {/* Inner white core */}
        {isWorking ? (
          <circle cx="340" cy="340" fill="#ffffff" filter={`url(#${id}-pt)`}>
            <animate attributeName="r"       values={`2;${maxCore};2`} dur={dur} calcMode="spline" keyTimes={kt} keySplines={ks} repeatCount="indefinite"/>
            <animate attributeName="opacity" values="1;0.92;1"   dur={dur} calcMode="spline" keyTimes={kt} keySplines={ks} repeatCount="indefinite"/>
          </circle>
        ) : !isIdle && (
          <circle cx="340" cy="340" r="28" fill="#ffffff" filter={`url(#${id}-pt)`}/>
        )}

        {/* Persistent brilliant point — always marks the origin */}
        <circle cx="340" cy="340" r="14" fill="#ffffff" filter={`url(#${id}-pt)`}>
          {isWorking && <animate attributeName="opacity" values="1;0.8;1" dur={dur} repeatCount="indefinite"/>}
        </circle>
      </g>
    </svg>
  );
}
